import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Truck as TruckIcon, Trash, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import TruckDiagram from '../components/TruckDiagram';
import MountTireModal from '../components/MountTireModal';
import DismountTireModal from '../components/DismountTireModal';
import { getTruckConfig } from '../lib/truckConfig';

export default function TruckDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [truck, setTruck] = useState(null);
  const [positions, setPositions] = useState([]);
  const [lifetimeKm, setLifetimeKm] = useState({});
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showMountModal, setShowMountModal] = useState(false);
  const [showDismountModal, setShowDismountModal] = useState(false);
  const [currentTire, setCurrentTire] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (profile && id) fetchTruckData(); }, [profile, id]);

  const fetchTruckData = async () => {
    try {
      const { data: truckData, error: truckError } = await supabase
        .from('trucks').select('*').eq('id', id).single();
      if (truckError) throw truckError;
      setTruck(truckData);

      const config = getTruckConfig(truckData.type);
      const wheelCount = truckData.num_wheels || config.wheels;

      const { data: posData, error: posError } = await supabase
        .from('tire_positions')
        .select('*, tires (id, serial_number, type, size, tread_depth, status)')
        .eq('truck_id', id)
        .is('dismounted_date', null)
        .order('position_number');
      if (posError) throw posError;

      const positionMap = {};
      posData.forEach(pos => { positionMap[pos.position_number] = pos; });

      const fullPositions = Array.from({ length: wheelCount }, (_, i) => {
        const posNum = i + 1;
        return positionMap[posNum] || { position_number: posNum, tire_id: null, tires: null };
      });
      setPositions(fullPositions);

      const mountedTireIds = posData.filter(p => p.tire_id).map(p => p.tire_id);
      if (mountedTireIds.length > 0) {
        const { data: historyRows } = await supabase
          .from('tire_positions')
          .select('tire_id, mounted_km, dismounted_km')
          .in('tire_id', mountedTireIds);
        const totals = {};
        (historyRows || []).forEach(p => {
          if (p.mounted_km != null && p.dismounted_km != null) {
            totals[p.tire_id] = (totals[p.tire_id] || 0) + (p.dismounted_km - p.mounted_km);
          }
        });
        setLifetimeKm(totals);
      } else { setLifetimeKm({}); }
    } catch (error) {
      console.error('Error fetching truck:', error);
      toast.error('Gagal memuat truk');
    }
  };

  const handlePositionClick = (position) => {
    if (!position) return;
    setSelectedPosition(position);
    if (position.tire_id) {
      setCurrentTire(position.tires);
      setShowDismountModal(true);
    } else { setShowMountModal(true); }
  };

  const handleMountComplete = () => {
    setShowMountModal(false); setSelectedPosition(null); fetchTruckData();
  };
  const handleDismountComplete = () => {
    setShowDismountModal(false); setSelectedPosition(null); setCurrentTire(null); fetchTruckData();
  };

  const mountedCount = positions.filter(p => p.tire_id).length;
  const canDelete = mountedCount === 0;

  const handleDeleteTruck = async () => {
    if (!canDelete) {
      toast.error('Tidak dapat menghapus: masih ada ban terpasang. Lepas semua ban terlebih dahulu.');
      return;
    }
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('trucks')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      toast.success(`Armada ${truck.truck_number} berhasil dihapus`);
      navigate('/trucks');
    } catch (error) {
      console.error('Error deleting truck:', error);
      toast.error('Gagal menghapus armada');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!truck) {
    return <div className="p-8"><p className="text-zinc-500 font-mono">Memuat data truk...</p></div>;
  }

  return (
    <div className="p-4 sm:p-8" data-testid="truck-detail-page">
      <button
        onClick={() => navigate('/trucks')}
        className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 font-mono uppercase text-xs tracking-wider mb-6 transition-colors rounded-lg"
        data-testid="back-button"
      >
        <ArrowLeft size={16} weight="bold" />
        KEMBALI KE TRUK
      </button>

      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Nomor Lambung</p>
          <h1 className="text-6xl sm:text-7xl font-black uppercase tracking-tight text-zinc-900 mb-3" style={{ fontFamily: 'Chivo' }}>
            {truck.truck_number}
          </h1>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Tipe</p>
              <p className="text-sm text-zinc-900 font-mono">{truck.type}</p>
            </div>
            {truck.custom_id && (
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">ID</p>
                <p className="text-sm text-zinc-900 font-mono">{truck.custom_id}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Roda</p>
              <p className="text-sm text-zinc-900 font-mono">{truck.num_wheels}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <TruckIcon size={48} weight="bold" className="text-zinc-300" />
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={!canDelete}
            title={canDelete ? 'Hapus armada ini' : 'Lepas semua ban terlebih dahulu'}
            className={`flex items-center gap-2 font-mono uppercase text-xs tracking-wider py-2 px-4 rounded-lg transition-colors ${
              canDelete
                ? 'border border-red-300 hover:bg-red-50 text-red-600'
                : 'border border-zinc-200 text-zinc-400 cursor-not-allowed'
            }`}
            data-testid="delete-truck-button"
          >
            <Trash size={14} weight="bold" />
            HAPUS ARMADA
          </button>
          {!canDelete && (
            <p className="text-[10px] font-mono text-amber-600 flex items-center gap-1 max-w-[200px] text-right">
              <Warning size={12} weight="bold" />
              Lepas {mountedCount} ban dulu
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-6" style={{ fontFamily: 'Chivo' }}>
            TATA LETAK RODA
          </h2>
          <TruckDiagram positions={positions} onPositionClick={handlePositionClick} truckType={truck.type} />
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-zinc-900 border-b border-zinc-200 pb-2 mb-6" style={{ fontFamily: 'Chivo' }}>
            INFO POSISI
          </h2>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4">Klik roda pada diagram</p>
          <div className="space-y-3">
            {positions.map((pos) => {
              const pastKm = pos.tire_id ? (lifetimeKm[pos.tire_id] || 0) : 0;
              return (
                <div key={pos.position_number} className="border-b border-zinc-200 pb-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-mono uppercase tracking-wider text-zinc-500">POS {pos.position_number}</p>
                    {pos.tires ? (
                      <p className="text-sm text-zinc-900 font-mono">{pos.tires.serial_number}</p>
                    ) : (
                      <p className="text-sm text-zinc-400 font-mono">— KOSONG —</p>
                    )}
                  </div>
                  {pos.tires && (
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Umur (KM)</p>
                      <p className="text-xs text-zinc-700 font-mono">{pastKm.toLocaleString('id-ID')}</p>
                    </div>
                  )}
                  {pos.tires && pos.mounted_km != null && (
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Pasang pada KM</p>
                      <p className="text-xs text-zinc-700 font-mono">{Number(pos.mounted_km).toLocaleString('id-ID')}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showMountModal && selectedPosition && (
        <MountTireModal
          truckId={truck.id} position={selectedPosition}
          onClose={() => setShowMountModal(false)} onComplete={handleMountComplete}
        />
      )}

      {showDismountModal && selectedPosition && currentTire && (
        <DismountTireModal
          truckId={truck.id} position={selectedPosition} currentTire={currentTire}
          pastLifetimeKm={lifetimeKm[currentTire.id] || 0}
          onClose={() => setShowDismountModal(false)} onComplete={handleDismountComplete}
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="delete-confirm-modal">
          <div className="bg-white border border-zinc-300 rounded-xl w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-zinc-200 flex items-center gap-3">
              <Warning size={28} weight="bold" className="text-red-600" />
              <h2 className="text-lg font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
                HAPUS ARMADA?
              </h2>
            </div>
            <div className="p-6">
              <p className="text-sm font-mono text-zinc-700 mb-4">
                Anda akan menghapus armada <span className="font-bold text-zinc-900">{truck.truck_number}</span>.
              </p>
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4">
                <p className="text-xs font-mono text-amber-800 leading-relaxed">
                  Riwayat ban (mount/dismount) tetap tersimpan untuk keperluan audit. Armada tidak akan muncul lagi di daftar.
                </p>
              </div>
              <p className="text-xs font-mono text-zinc-500">Tindakan ini hanya dapat dibatalkan oleh administrator melalui database.</p>
            </div>
            <div className="p-6 border-t border-zinc-200 flex gap-3">
              <button
                onClick={handleDeleteTruck}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-mono uppercase text-xs tracking-wider py-3 px-4 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                data-testid="confirm-delete-truck"
              >
                {deleting ? 'MENGHAPUS...' : 'YA, HAPUS'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 border border-zinc-300 hover:bg-zinc-100 text-zinc-900 font-mono uppercase text-xs tracking-wider py-3 px-4 rounded-lg transition-colors"
                data-testid="cancel-delete-truck"
              >
                BATAL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
